import { Pie, PieChart, ResponsiveContainer } from "recharts"

import { chartConfig } from "@/config/charts"
import { cn } from "@/lib/utils"
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

interface PieChartProps {
  data: any
  dataKey: string
  config?: ChartConfig
  nameKey: string
  className?: string
  label?: string
}

const CustomLabel = ({ cx, cy, midAngle, outerRadius, percent, name }: any) => {
  const RADIAN = Math.PI / 180
  const radius = outerRadius * 1.1
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)

  return (
    <text
      x={x}
      y={y}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      className="text-[10px] font-medium dark:text-white md:text-[12px]"
      fill="currentColor"
    >
      {`${name} (${(percent * 100).toFixed(2)}%)`}
    </text>
  )
}

export function PieChartComponent({
  data,
  config,
  dataKey,
  nameKey,
  className,
  label,
}: PieChartProps) {
  /*   const coloredData = (data || []).map((entry: any) => ({
    ...entry,
    fill: chartConfig[entry[nameKey] as keyof typeof chartConfig]?.color,
  })) */
  const coloredData = Array.isArray(data)
    ? (data || [])?.map((entry: any) => {
        const sanitizedKey = entry[nameKey]?.trim().replace(/\s+/g, "") // Remove spaces
        //console.log(sanitizedKey);

        return {
          ...entry,
          fill: config
            ? config[sanitizedKey as keyof typeof config]?.color
            : chartConfig[sanitizedKey as keyof typeof chartConfig]?.color,
        }
      })
    : []

  const totalValue = Array.isArray(data)
    ? (data || []).reduce((acc: number, entry: any) => acc + entry[dataKey], 0)
    : []

  const seriesKeys =
    data && data.length > 0
      ? Object.keys(data[0]).filter(
          (key) =>
            key !== "time" && chartConfig[key as keyof typeof chartConfig],
        )
      : []

  return (
    <ChartContainer
      config={config ? config : chartConfig}
      className={cn("aspect-16/9 mx-auto h-[50dvh] w-full", className)}
    >
      {/* <ResponsiveContainer width="100%" height="100%"> */}
      <PieChart>
        {totalValue > 0 ? (
          <>
            <ChartTooltip
              content={<ChartTooltipContent nameKey={nameKey} hideLabel />}
              formatter={(value: number, name: string) => {
                const label =
                  chartConfig[name as keyof typeof chartConfig]?.label || name
                return [`${label} Counts: `, value]
              }}
            />
            <defs>
              {seriesKeys.map((key) => (
                <linearGradient
                  id={`color-${key}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                  key={key}
                >
                  <stop
                    offset="0%"
                    stopColor={
                      chartConfig[key as keyof typeof chartConfig].color
                    }
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="50%"
                    stopColor={
                      chartConfig[key as keyof typeof chartConfig].color
                    }
                    stopOpacity={0.5}
                  />
                  <stop
                    offset="100%"
                    stopColor={
                      chartConfig[key as keyof typeof chartConfig].color
                    }
                    stopOpacity={0.1}
                  />
                </linearGradient>
              ))}
            </defs>

            <Pie
              data={coloredData}
              dataKey={dataKey}
              nameKey={nameKey}
              outerRadius="75%"
              innerRadius="50%"
              cornerRadius={8}
              paddingAngle={4}
              stroke="none"
              label={(props) => <CustomLabel {...props} name={props.name} />}
              labelLine
            />
          </>
        ) : (
          <Pie
            data={[
              {
                [nameKey]: "NaN",
                [dataKey]: 1,
              },
            ]}
            dataKey={dataKey}
          />
        )}
        <ChartLegend
          content={<ChartLegendContent nameKey={nameKey} />}
          className="mt-2 flex flex-wrap items-center gap-2"
        />
      </PieChart>
      {/* </ResponsiveContainer> */}
    </ChartContainer>
  )
}
